import type { EnrollmentTokenResponse, PartnerEnvironment as Env, Topology } from '@central/api';
import {
  Card,
  HealthChip,
  InfoRow,
  KpiTile,
  PageHeader,
  UptimeStrip,
  color,
  font,
  formatAgo,
  formatDate,
  formatPercent,
} from '@central/ui';
import {
  Alert,
  Button,
  Code,
  CopyButton,
  Group,
  Modal,
  Select,
  Table,
  TextInput,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { keys, useApi } from '../../api';
import { CustomerSelect } from '../../components/CustomerSelect';
import { Crumbs, DataTable, Empty, QueryState } from '../../components/parts';
import { errorMessage } from '../../lib/errors';
import { enrollmentLabel, mono, muted } from '../../lib/labels';
import { latestInstaller } from '../../lib/releases';
import { InstallerLink } from './PartnerReleases';

const topologies: Array<{ value: Topology; label: string }> = [
  { value: 'standalone', label: 'Standalone' },
  { value: 'compose', label: 'Docker Compose' },
  { value: 'kubernetes', label: 'Kubernetes' },
];

/** The reseller's environments: its customers' dedicated installations. */
export function PartnerEnvironments() {
  const search = useSearch({ from: '/app/partner/environments' });
  const navigate = useNavigate({ from: '/partner/environments' });
  const api = useApi();
  const customerId = search.customerId ?? '';
  const list = useQuery({
    queryKey: keys.partnerEnvironments(customerId),
    queryFn: () => api.partner.environments.list(customerId ? { customerId } : {}),
  });
  const set = (next: { customerId?: string; new?: boolean }) =>
    void navigate({ search: (s) => ({ ...s, ...next }), replace: true });
  return (
    <>
      <PageHeader
        title="Environments"
        subtitle="Your customers’ GoATMS installations: add one, enroll it, watch its health"
        actions={
          <Button color="dark" size="sm" h={40} onClick={() => set({ new: true })}>
            New environment
          </Button>
        }
      />
      <CustomerSelect
        label="Customer"
        placeholder="Every customer"
        w={280}
        value={customerId}
        onChange={(id) => set({ customerId: id || undefined })}
      />
      <Card padded={false}>
        <QueryState isPending={list.isPending} error={list.error}>
          {() =>
            list.data!.items.length === 0 ? (
              <Empty>No environments yet.</Empty>
            ) : (
              <DataTable
                label="Environments"
                minWidth={720}
                head={['Name', 'Customer', 'Health', 'Version', 'Enrollment', 'Last report']}
              >
                {list.data!.items.map((e) => (
                  <Table.Tr key={e.id}>
                    <Table.Td style={{ fontWeight: 600 }}>
                      <Link
                        to="/partner/environments/$environmentId"
                        params={{ environmentId: e.id }}
                      >
                        {e.name}
                      </Link>
                    </Table.Td>
                    <Table.Td>{e.customer.name}</Table.Td>
                    <Table.Td>
                      <HealthChip state={e.health} />
                    </Table.Td>
                    <Table.Td style={mono}>{e.version ?? '–'}</Table.Td>
                    <Table.Td>{enrollmentLabel[e.enrollment.status]}</Table.Td>
                    <Table.Td style={muted}>
                      {e.lastHeartbeatAt ? formatAgo(e.lastHeartbeatAt) : 'Never'}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </DataTable>
            )
          }
        </QueryState>
      </Card>
      {search.new && (
        <NewEnvironment
          customerId={customerId}
          onClose={() => set({ new: undefined })}
          onCreated={(e) =>
            void navigate({
              to: '/partner/environments/$environmentId',
              params: { environmentId: e.id },
              search: {},
            })
          }
        />
      )}
    </>
  );
}

/** Splits "a, b c" into tenant ids. */
const tenantIds = (v: string) => v.split(/[\s,]+/).filter(Boolean);

function NewEnvironment({
  customerId: initial,
  onClose,
  onCreated,
}: {
  customerId: string;
  onClose: () => void;
  onCreated: (e: Env) => void;
}) {
  const api = useApi();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [customerId, setCustomerId] = useState(initial);
  const [topology, setTopology] = useState<Topology>('standalone');
  const [tenants, setTenants] = useState('default');
  const [problems, setProblems] = useState<Record<string, string>>({});
  const create = useMutation({
    mutationFn: () =>
      api.partner.environments.create({
        name: name.trim(),
        customerId,
        topology,
        tenantIds: tenantIds(tenants),
      }),
    onSuccess: async (e) => {
      await qc.invalidateQueries({ queryKey: keys.partner });
      onCreated(e);
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const p: Record<string, string> = {};
    if (!name.trim()) p.name = 'Name the environment.';
    if (!customerId) p.customerId = 'Choose the customer.';
    if (tenantIds(tenants).length === 0) p.tenants = 'At least one tenant id.';
    setProblems(p);
    if (Object.keys(p).length === 0) create.mutate();
  };
  return (
    <Modal opened onClose={onClose} title="New environment">
      <form
        onSubmit={submit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <TextInput
          label="Name"
          placeholder="Production"
          data-autofocus
          value={name}
          error={problems.name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        <CustomerSelect
          label="Customer"
          placeholder="Choose a customer"
          value={customerId}
          error={problems.customerId}
          onChange={setCustomerId}
        />
        <Select
          label="Topology"
          data={topologies}
          value={topology}
          allowDeselect={false}
          onChange={(v) => v && setTopology(v as Topology)}
        />
        <TextInput
          label="GoATMS tenant ids"
          description="Separated by commas. The installation binds them to the customer’s license when it activates."
          value={tenants}
          error={problems.tenants}
          onChange={(e) => setTenants(e.currentTarget.value)}
        />
        {create.error && <Alert color="red">{errorMessage(create.error)}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" color="dark" loading={create.isPending}>
            Create environment
          </Button>
        </Group>
      </form>
    </Modal>
  );
}

/** One environment: how to install and enroll it, then its health. */
export function PartnerEnvironment() {
  const { environmentId } = useParams({ from: '/app/partner/environments/$environmentId' });
  const api = useApi();
  const q = useQuery({
    queryKey: keys.partnerEnvironment(environmentId),
    queryFn: () => api.partner.environments.get(environmentId),
  });
  return (
    <QueryState isPending={q.isPending} error={q.error}>
      {() => {
        const e = q.data!;
        return (
          <>
            <Crumbs
              items={[
                { label: 'Environments', to: '/partner/environments' },
                {
                  label: e.customer.name,
                  to: '/partner/customers/$customerId',
                  params: { customerId: e.customer.id },
                },
                { label: e.name },
              ]}
            />
            <PageHeader title={e.name} subtitle={<HealthChip state={e.health} />} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start' }}>
              <div style={{ flex: '1 1 380px', minWidth: 0 }}>
                <Install e={e} />
              </div>
              <div style={{ flex: '1 1 380px', minWidth: 0 }}>
                <Card title="Details">
                  <InfoRow label="Customer">{e.customer.name}</InfoRow>
                  <InfoRow label="Topology">{e.topology ?? '–'}</InfoRow>
                  <InfoRow label="Version">
                    <span style={mono}>{e.version ?? '–'}</span>
                  </InfoRow>
                  <InfoRow label="Tenants">
                    <span style={mono}>{e.tenantIds.join(', ') || '–'}</span>
                  </InfoRow>
                  <InfoRow label="Enrollment">{enrollmentLabel[e.enrollment.status]}</InfoRow>
                  <InfoRow label="Last report">
                    {e.lastHeartbeatAt ? formatAgo(e.lastHeartbeatAt) : 'Never'}
                  </InfoRow>
                  {e.nodeCount !== undefined && <InfoRow label="Nodes">{e.nodeCount}</InfoRow>}
                  {e.nextUpgrade && (
                    <InfoRow label="Next upgrade">
                      {e.nextUpgrade.version} · {formatDate(e.nextUpgrade.startsAt)}
                    </InfoRow>
                  )}
                </Card>
              </div>
            </div>
            {e.enrollment.status === 'enrolled' && (
              <>
                <Uptime environmentId={e.id} />
                <Services environmentId={e.id} />
              </>
            )}
          </>
        );
      }}
    </QueryState>
  );
}

/** Download the installer, then enroll with a one-time token. */
function Install({ e }: { e: Env }) {
  const api = useApi();
  const qc = useQueryClient();
  const releases = useQuery({
    queryKey: keys.partnerReleases,
    queryFn: () => api.partner.releases(),
  });
  // The token is shown once: held only in this component, never in the query cache.
  const [token, setToken] = useState<EnrollmentTokenResponse>();
  const issue = useMutation({
    mutationFn: () => api.partner.environments.enrollmentToken(e.id),
    onSuccess: async (t) => {
      setToken(t);
      await qc.invalidateQueries({ queryKey: keys.partnerEnvironment(e.id) });
    },
  });
  const latest = latestInstaller(releases.data?.items);
  const canEnroll = e.enrollment.status === 'not_enrolled' || e.enrollment.status === 'revoked';
  return (
    <Card title="Install">
      <ol style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <li>
          <div style={{ fontSize: 14, marginBottom: 6 }}>Download the GoATMS installer.</div>
          {latest ? (
            <InstallerLink r={latest} />
          ) : (
            <span style={{ fontSize: 13, ...muted }}>
              No installer is published yet. See{' '}
              <Link to="/partner/releases">Releases & installers</Link>.
            </span>
          )}
        </li>
        <li>
          <div style={{ fontSize: 14, marginBottom: 6 }}>
            Enroll the installation with a one-time token.
          </div>
          {!canEnroll ? (
            <span style={{ fontSize: 13, ...muted }}>
              {e.enrollment.status === 'enrolled'
                ? `Enrolled${e.enrollment.enrolledAt ? ` ${formatDate(e.enrollment.enrolledAt)}` : ''}.`
                : 'Waiting for Rethink to approve this installation.'}
            </span>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {token ? (
                <div
                  aria-label="Enrollment token"
                  style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                >
                  <span style={{ fontSize: 13, color: color.textBody }}>
                    Enter this token in the GoATMS installer or License page. It is shown once,
                    works once, and expires {formatDate(token.expiresAt)}.
                  </span>
                  <Code
                    block
                    style={{
                      wordBreak: 'break-all',
                      whiteSpace: 'pre-wrap',
                      fontFamily: font.mono,
                    }}
                  >
                    {token.token}
                  </Code>
                  <CopyButton value={token.token}>
                    {({ copied, copy }) => (
                      <Button variant="default" size="xs" onClick={copy}>
                        {copied ? 'Copied' : 'Copy token'}
                      </Button>
                    )}
                  </CopyButton>
                </div>
              ) : (
                <span style={{ fontSize: 13, ...muted }}>
                  Issuing a new token invalidates any unused one.
                </span>
              )}
              <Button
                variant="default"
                loading={issue.isPending}
                onClick={() => issue.mutate()}
                style={{ alignSelf: 'flex-start' }}
              >
                {token ? 'Issue a new token' : 'Create enrollment token'}
              </Button>
              {issue.error && <Alert color="red">{errorMessage(issue.error)}</Alert>}
            </div>
          )}
        </li>
        <li style={{ fontSize: 14 }}>
          Once enrolled, the installation reports every minute and its health shows here.
        </li>
      </ol>
    </Card>
  );
}

function Uptime({ environmentId }: { environmentId: string }) {
  const api = useApi();
  const uptime = useQuery({
    queryKey: keys.partnerEnvironmentUptime(environmentId),
    queryFn: () => api.partner.environments.uptime(environmentId, { days: 90 }),
  });
  const u = uptime.data?.uptime;
  const days = uptime.data?.days ?? [];
  const pct = (v: number | undefined) => (
    <span style={{ fontFamily: font.mono, fontWeight: 500 }}>{formatPercent(v)}</span>
  );
  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 12,
        }}
      >
        <KpiTile label="Uptime · 24 h" value={pct(u?.d1)} />
        <KpiTile label="Uptime · 7 d" value={pct(u?.d7)} />
        <KpiTile label="Uptime · 30 d" value={pct(u?.d30)} />
        <KpiTile label="Uptime · 90 d" value={pct(u?.d90)} />
      </div>
      {days.length > 0 && (
        <Card title={`Last ${days.length} days`}>
          <UptimeStrip days={days} height={32} />
        </Card>
      )}
    </>
  );
}

function Services({ environmentId }: { environmentId: string }) {
  const api = useApi();
  const q = useQuery({
    queryKey: keys.partnerEnvironmentServices(environmentId),
    queryFn: () => api.partner.environments.services(environmentId),
  });
  return (
    <Card title="Services" padded={false}>
      <QueryState isPending={q.isPending} error={q.error}>
        {() =>
          q.data!.items.length === 0 ? (
            <Empty>No report kept yet.</Empty>
          ) : (
            <DataTable
              label="Services"
              minWidth={520}
              head={['Service', 'State', 'Ready', 'Version']}
            >
              {q.data!.items.map((s) => (
                <Table.Tr key={s.name}>
                  <Table.Td style={{ fontWeight: s.core ? 600 : 400 }}>{s.name}</Table.Td>
                  <Table.Td>
                    <HealthChip state={s.state} />
                  </Table.Td>
                  <Table.Td style={mono}>
                    {s.ready}/{s.desired}
                  </Table.Td>
                  <Table.Td style={mono}>{s.version ?? '–'}</Table.Td>
                </Table.Tr>
              ))}
            </DataTable>
          )
        }
      </QueryState>
    </Card>
  );
}
